import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { NotificationBingOutline } from '@aslanpay/portal-common-lib';
import useAuthContext from 'app/hooks/useAuthContext';
import useInAppNotifications from 'app/hooks/useInAppNotifications';
import useIsPotManager from 'app/hooks/useIsPotManager';
import useRecognition from 'app/hooks/useRecognition';
import { getInAppNotificationPath } from 'app/utils/inAppNotifications';
import { useNavigate } from 'react-router-dom';

function NotificationMenu() {
  const { user } = useAuthContext();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const navigate = useNavigate();
  const { isPotManager } = useIsPotManager();
  const { data: unreadNotifications } = useInAppNotifications.useFetchInAppNotifications(
    user?.id
      ? {
          individualId: user.id,
          isRead: false,
          page: 0,
          size: 5,
          sortBy: 'createdAt',
          sort: 'DESC',
        }
      : undefined,
    !!user?.id
  );
  const { data: pendingApprovals } = useRecognition.useListPendingApprovals(
    { page: 0, size: 1 },
    isPotManager
  );
  const { mutate: markAsRead } = useInAppNotifications.useMarkInAppNotificationAsRead();
  const { mutate: markAllAsRead, isPending: isMarkingAllAsRead } =
    useInAppNotifications.useMarkAllInAppNotificationsAsRead();

  const unreadCount = unreadNotifications?.total ?? 0;
  const approvalCount = isPotManager ? pendingApprovals?.total ?? 0 : 0;
  const badgeCount = unreadCount + approvalCount;

  return (
    <Menu isOpen={isOpen} placement="bottom-end" onClose={onClose} onOpen={onOpen}>
      <Box position="relative">
        <MenuButton
          as={IconButton}
          aria-label="Notifications"
          data-testid="notification-bell"
          variant="ghost"
          boxSize="28px"
          borderRadius="3px"
          p="0"
          icon={<NotificationBingOutline boxSize="20px" />}
        />
        {badgeCount > 0 && (
          <Box
            as="span"
            position="absolute"
            top="-2px"
            right="-4px"
            borderRadius="full"
            border="1px solid"
            borderColor="white"
            bg="red.500"
            color="white"
            minW="14px"
            h="14px"
            px="3px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="8px"
            fontWeight="700"
            lineHeight="1"
            pointerEvents="none"
            data-testid="notification-badge"
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </Box>
        )}
      </Box>
      <MenuList minW={{ base: '300px', sm: '380px' }} maxW="calc(100vw - 24px)" p="8px">
        <Flex justify="space-between" align="center" px="8px" py="6px">
          <Text fontWeight="700" color="brand.darkPurple">
            Notifications
          </Text>
          <Flex align="center" gap="12px">
            {unreadCount > 0 && user?.id && (
              <Button
                variant="link"
                size="xs"
                isLoading={isMarkingAllAsRead}
                onClick={() => markAllAsRead(user.id)}
              >
                Mark all as read
              </Button>
            )}
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                onClose();
                navigate('/notifications');
              }}
            >
              View all
            </Button>
          </Flex>
        </Flex>
        {approvalCount > 0 && (
          <>
            <MenuItem
              borderRadius="6px"
              onClick={() => navigate('/reward-and-recognition/approvals?tab=pendingApprovals')}
            >
              <Flex justify="space-between" w="full" gap="16px">
                <Text fontWeight="600">Recognitions awaiting approval</Text>
                <Box
                  as="span"
                  minW="20px"
                  h="20px"
                  px="5px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  bg="brand.lightPurple"
                  color="brand.purple"
                  fontWeight="700"
                  fontSize="11px"
                >
                  {approvalCount}
                </Box>
              </Flex>
            </MenuItem>
            <Divider my="6px" />
          </>
        )}
        {(unreadNotifications?.list ?? []).map((notification) => (
          <MenuItem
            key={notification.id}
            borderRadius="6px"
            whiteSpace="normal"
            onClick={() => {
              markAsRead(notification.id);
              navigate(getInAppNotificationPath(notification.type));
            }}
          >
            <Box>
              <Text fontWeight="600" color="brand.darkPurple">
                {notification.title}
              </Text>
              <Text fontSize="sm" color="brand.darkGray" noOfLines={2}>
                {notification.body}
              </Text>
            </Box>
          </MenuItem>
        ))}
        {unreadCount === 0 && approvalCount === 0 && (
          <Text px="8px" py="18px" textAlign="center" color="brand.gray">
            You are all caught up.
          </Text>
        )}
      </MenuList>
    </Menu>
  );
}

export default NotificationMenu;
